import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { supabase } from '@/integrations/supabase/client';

interface LeadRow {
  id: string;
  created_at: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  lead_status: 'Open'|'Closed';
  broker_status: 'Awaiting Broker'|'Broker Made Contact'|'Quote Sent to Client'|'Client Accepted Quote'|'Client Paid';
  broker_comments?: string | null;
}

export const TenantLeadStatusList: React.FC = () => {
  const { tenant } = useTenant();
  const [rows, setRows] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!tenant) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('leads')
          .select(`
            id, created_at, lead_status, broker_status, broker_comments,
            lead_contacts(name, email, phone)
          `)
          .eq('tenant_id', tenant.id)
          .order('created_at', { ascending: false });
        if (error) throw error;
        const mapped: LeadRow[] = (data || []).map((r: any) => ({
          id: r.id,
          created_at: r.created_at,
          lead_status: r.lead_status || 'Open',
          broker_status: r.broker_status || 'Awaiting Broker',
          broker_comments: r.broker_comments,
          contact_name: r.lead_contacts?.[0]?.name,
          contact_email: r.lead_contacts?.[0]?.email,
          contact_phone: r.lead_contacts?.[0]?.phone,
        }));
        setRows(mapped);
      } catch (e) {
        console.error('Failed to load tenant leads status', e);
      } finally {
        setLoading(false);
      }
    };

    load();

    // realtime updates for this tenant's leads
    if (!tenant) return;
    const channel = supabase
      .channel(`tenant-leads-${tenant.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'leads', filter: `tenant_id=eq.${tenant.id}` }, (payload: any) => {
        const row = payload.new;
        setRows(prev => prev.map(r => r.id === row.id ? {
          ...r,
          lead_status: row.lead_status || r.lead_status,
          broker_status: row.broker_status || r.broker_status,
          broker_comments: row.broker_comments,
        } : r));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [tenant]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lead Progress</CardTitle>
        <CardDescription>Live status of the leads you referred to us</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Lead Status</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Broker Comments</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin inline-block mr-2" /> Loading...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No leads found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="font-medium">{r.contact_name || 'N/A'}</div>
                      <div className="text-xs text-muted-foreground">{r.contact_email || ''}</div>
                      <div className="text-xs text-muted-foreground">{r.contact_phone || ''}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.lead_status === 'Open' ? 'secondary' : 'destructive'}>{r.lead_status}</Badge>
                    </TableCell>
                    <TableCell>{r.broker_status}</TableCell>
                    <TableCell className="max-w-[420px] whitespace-pre-wrap text-sm">{r.broker_comments || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

